import { useCallback, useEffect, useRef, useState } from "react";

const isValidYear = (value) =>
  typeof value === "number" && Number.isFinite(value);

const clampYear = (value, min, max) => Math.min(Math.max(value, min), max);

const buildSelection = (current, minYear, maxYear, defaults = {}) => {
  if (!isValidYear(minYear) || !isValidYear(maxYear)) {
    return { from: null, to: null };
  }
  let from = isValidYear(current?.from)
    ? current.from
    : isValidYear(defaults.from)
    ? defaults.from
    : minYear;
  let to = isValidYear(current?.to)
    ? current.to
    : isValidYear(defaults.to)
    ? defaults.to
    : maxYear;
  from = clampYear(from, minYear, maxYear);
  to = clampYear(to, minYear, maxYear);
  if (from > to) {
    from = to;
  }
  return { from, to };
};

export default function useHistorySelection({
  range,
  debounceMs = 300,
  defaultFrom,
  defaultTo,
} = {}) {
  const [draft, setDraft] = useState({ from: null, to: null });
  const [applied, setApplied] = useState({ from: null, to: null });
  const draftRef = useRef(draft);
  const appliedRef = useRef(applied);
  const timerRef = useRef(null);

  const minYear = isValidYear(range?.minYear) ? range.minYear : null;
  const maxYear = isValidYear(range?.maxYear) ? range.maxYear : null;
  const hasRange = isValidYear(minYear) && isValidYear(maxYear);

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  useEffect(() => {
    draftRef.current = draft;
  }, [draft]);

  useEffect(() => {
    appliedRef.current = applied;
  }, [applied]);

  useEffect(() => {
    clearTimer();
    if (!hasRange) {
      const empty = { from: null, to: null };
      draftRef.current = empty;
      appliedRef.current = empty;
      setDraft(empty);
      setApplied(empty);
      return;
    }
    const defaults = { from: defaultFrom, to: defaultTo };
    const nextDraft = buildSelection(draftRef.current, minYear, maxYear, defaults);
    const nextApplied = buildSelection(appliedRef.current, minYear, maxYear, defaults);
    draftRef.current = nextDraft;
    appliedRef.current = nextApplied;
    setDraft(nextDraft);
    setApplied(nextApplied);
  }, [clearTimer, hasRange, minYear, maxYear, defaultFrom, defaultTo]);

  const updateDraft = useCallback(
    (partial = {}) => {
      if (!hasRange) return;
      const prev = draftRef.current || {};
      const prevFrom = isValidYear(prev.from) ? prev.from : minYear;
      const prevTo = isValidYear(prev.to) ? prev.to : maxYear;
      let nextFrom = isValidYear(partial.from) ? partial.from : prevFrom;
      let nextTo = isValidYear(partial.to) ? partial.to : prevTo;

      nextFrom = clampYear(nextFrom, minYear, maxYear);
      nextTo = clampYear(nextTo, minYear, maxYear);

      if (nextFrom > nextTo) {
        if (isValidYear(partial.from)) {
          nextFrom = nextTo;
        } else {
          nextTo = nextFrom;
        }
      }

      const next = { from: nextFrom, to: nextTo };
      draftRef.current = next;
      setDraft(next);

      if (debounceMs <= 0) {
        appliedRef.current = next;
        setApplied(next);
        return;
      }

      clearTimer();
      timerRef.current = setTimeout(() => {
        timerRef.current = null;
        appliedRef.current = draftRef.current;
        setApplied(draftRef.current);
      }, debounceMs);
    },
    [clearTimer, debounceMs, hasRange, maxYear, minYear]
  );

  const commitDraft = useCallback(() => {
    if (!hasRange) return;
    clearTimer();
    appliedRef.current = draftRef.current;
    setApplied(draftRef.current);
  }, [clearTimer, hasRange]);

  useEffect(
    () => () => {
      clearTimer();
    },
    [clearTimer]
  );

  return { draft, applied, updateDraft, commitDraft };
}
